/**
 * Paddle.js (Billing v2) checkout for the optional Supporter membership.
 *
 * Loads Paddle.js once from the official CDN, initializes it with the PUBLIC client-side token
 * (never a secret - see backend/paddle.jac), and opens the overlay checkout for a price. The
 * subscription is bound to the LibreDraw account via customData.root_jid, which the server
 * re-checks in verify-by-retrieval (MySubscription). Entitlement is NEVER granted from the
 * success callback here - it only nudges the server to re-verify.
 *
 * Must be triggered from a user gesture (a click). The overlay is an injected iframe, not a
 * window.open popup, so it isn't blocked by popup blockers, but Paddle still expects a
 * user-initiated open.
 */

const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

export type CheckoutCompleted = {
  customerId?: string;
  transactionId?: string;
  subscriptionId?: string;
};

export type CheckoutOpts = {
  clientToken: string;
  environment: string; // "sandbox" | "production"
  priceId: string;
  rootJid: string;
  email?: string;
  onCompleted: (data: CheckoutCompleted) => void;
  onClose?: () => void;
  onError?: (message: string) => void;
};

let loading: Promise<any> | null = null;
let initialized = false;
let currentOpts: CheckoutOpts | null = null;

/** Load Paddle.js once; resolve with the global Paddle object. */
function loadPaddle(): Promise<any> {
  const w = window as any;
  if (w.Paddle) return Promise.resolve(w.Paddle);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PADDLE_JS;
    s.async = true;
    s.onload = () => {
      const p = (window as any).Paddle;
      if (p) resolve(p);
      else reject(new Error("paddle_unavailable"));
    };
    s.onerror = () => {
      loading = null; // allow a later retry
      reject(new Error("paddle_load_failed"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * Open the Supporter checkout overlay for a single price. Resolves once the overlay has been
 * asked to open; the real outcome arrives via opts.onCompleted (checkout.completed).
 */
export async function openSupporterCheckout(opts: CheckoutOpts): Promise<void> {
  const Paddle = await loadPaddle();
  currentOpts = opts;
  if (!initialized) {
    Paddle.Environment.set(opts.environment === "production" ? "production" : "sandbox");
    Paddle.Initialize({
      token: opts.clientToken,
      eventCallback: (ev: any) => {
        const name = ev && ev.name;
        if (name === "checkout.completed") {
          const d = (ev && ev.data) || {};
          const cust = d.customer || {};
          if (currentOpts) {
            currentOpts.onCompleted({
              customerId: cust.id,
              transactionId: d.transaction_id || d.id,
              subscriptionId: d.subscription_id,
            });
          }
        } else if (name === "checkout.closed") {
          if (currentOpts && currentOpts.onClose) currentOpts.onClose();
        } else if (name === "checkout.error" || name === "checkout.failed") {
          // Surface the failure instead of leaving the overlay to die silently (e.g. Paddle
          // rejecting the transaction because the account's default payment link isn't set).
          const msg = (ev && ev.error && (ev.error.detail || ev.error.message)) || (ev && ev.data && ev.data.message) || "";
          if (currentOpts && currentOpts.onError) currentOpts.onError(String(msg || "checkout_error"));
        }
      },
    });
    initialized = true;
  }
  const checkout: any = {
    items: [{ priceId: opts.priceId, quantity: 1 }],
    customData: { root_jid: opts.rootJid },
    settings: { displayMode: "overlay", theme: "light", showAddDiscounts: false },
  };
  if (opts.email) checkout.customer = { email: opts.email };
  Paddle.Checkout.open(checkout);
}
